import {
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  ChevronRight,
  DeleteOutline,
  EditOutlined,
  InfoOutlined,
  OpenInNew,
  PushPin,
  PushPinOutlined,
} from '@mui/icons-material';
import { Box, Button, IconButton, styled, Tooltip } from '@mui/material';
import {
  DataGrid,
  getGridStringOperators,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AnalyticsEvent, useAnalytics } from '../../components/Providers/AnalyticsProvider';
import { GRAPH_DOC_STATUS_CONFIG } from '../../constants/GraphRagConstants';
import { copyFile, deleteFile, updateFile } from '../../redux/actions/fileActions';
import { myFolder } from '../../redux/consts';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { AppDispatch } from '../../redux/store';
import { StyledSkeleton } from '../../theme/CustomComponents';
import { FileView, GraphDocStatus, IndexType } from '../../Types/enums';
import { CustomFile } from '../../Types/file';
import { formatDate } from '../../util/dateUtils';
import AutoTruncatingTypographyWithTooltip from '../AutoTruncatingTypographyWithTooltip';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import NameDialog from '../NameDialog';
import { useAuth } from '../Providers/AuthProvider';

const StyledLink = styled('a')({
  color: 'inherit',
  textDecoration: 'none',
  ':hover': {
    textDecoration: 'underline',
  },
});

export type FileTableProps = {
  files: CustomFile[];
  fileView: FileView;
  setIsLoadingThis: (isLoading: boolean) => void;
  onFileChange?: () => void;
  total?: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  isLoading?: boolean;
  onGoToGraph?: (graphDocId: string) => void;
};

const FileTable = ({
  files,
  fileView,
  setIsLoadingThis,
  onFileChange,
  total = files?.length || 0,
  paginationModel,
  onPaginationModelChange,
  filterModel: externalFilterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  isLoading = false,
  onGoToGraph,
}: FileTableProps): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useAnalytics();
  const { currentUser } = useAuth();
  const { currentFolder } = useSelector(selectFolders);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fileToRename, setFileToRename] = useState<CustomFile | null>(null);
  const [fileToDelete, setFileToDelete] = useState<CustomFile | null>(null);
  const [internalFilterModel, setInternalFilterModel] = useState<GridFilterModel>({ items: [] });
  const isGraphRagIndex = currentFolder?.project?.indexToUse === IndexType.GRAPH_RAG;

  const handleToggleFilePin = useCallback(
    (fileId: string, pinned: boolean) => {
      analytics.event(
        AnalyticsEvent.WM_NIGEL_MY_FILES_TOGGLE_PIN,
        {
          id: fileId,
          value: `Pinned=${pinned}`,
        },
        {
          fileId,
          fileWasPinned: pinned,
          fileNowPinned: !pinned,
          ...(fileView === FileView.FOLDER ? { folderId: currentFolder?.id } : {}),
        }
      );
      setIsLoadingThis(true);
      dispatch(updateFile({ fileId, file: { pinned: !pinned } })).then(() => {
        setIsLoadingThis(false);
        if (onFileChange) {
          onFileChange();
        }
      });
    },
    [analytics, dispatch, onFileChange, setIsLoadingThis, currentFolder?.id, fileView]
  );

  const handleFileRename = (fileId: string, newFileName: string) => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_MY_FILES_RENAME,
      {
        id: fileId,
        value: `Name=${newFileName}`,
      },
      {
        fileId,
        oldFileName: fileToRename?.fileName,
        newFileName,
        ...(fileView === FileView.FOLDER ? { folderId: currentFolder?.id } : {}),
      }
    );
    setIsLoadingThis(true);
    dispatch(updateFile({ fileId, file: { fileName: newFileName } })).then(() => {
      setIsLoadingThis(false);
      if (onFileChange) {
        onFileChange();
      }
      setIsRenaming(false);
      setFileToRename(null);
    });
  };

  const handleFileDelete = (fileId: string) => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_MY_FILES_DELETE,
      { id: fileId },
      { fileId, ...(fileView === FileView.FOLDER ? { folderId: currentFolder?.id } : {}) }
    );
    setIsLoadingThis(true);
    dispatch(deleteFile(fileId)).then(() => {
      setIsLoadingThis(false);
      if (onFileChange) {
        onFileChange();
      }

      setIsDeleting(false);
      setFileToDelete(null);
    });
  };

  const handleCopyFileTo = useCallback(
    (fileId: string, destination: 'user' | 'folder') => {
      analytics.event(AnalyticsEvent.WM_NIGEL_MY_FILES_COPY_TO, undefined, {
        fileId,
        ...(destination === 'folder'
          ? { folderId: currentFolder?.id }
          : { userId: currentUser?.id }),
      });
      setIsLoadingThis(true);
      dispatch(
        copyFile({ fileId, folderId: destination === 'folder' ? currentFolder?.id : undefined })
      ).then(() => {
        setIsLoadingThis(false);
        if (onFileChange) {
          onFileChange();
        }
      });
    },
    [dispatch, onFileChange, setIsLoadingThis, currentFolder?.id, analytics, currentUser?.id]
  );

  const renderGraphDocStatusIcon = (status: GraphDocStatus) => {
    const {
      icon: Icon,
      color,
      tooltip,
    } = GRAPH_DOC_STATUS_CONFIG[status] ?? GRAPH_DOC_STATUS_CONFIG[GraphDocStatus.PENDING];

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
          }}
        >
          <Icon color={color} fontSize="small" />
        </Box>

        <Tooltip title={tooltip} arrow>
          <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5 }} />
        </Tooltip>
      </Box>
    );
  };

  const customOperators = useMemo(
    () =>
      getGridStringOperators().filter((op) =>
        ['contains', 'equals', 'startsWith', 'endsWith'].includes(op.value)
      ),
    []
  );

  const columns = useMemo<GridColDef<CustomFile>[]>(() => {
    const baseColumns: GridColDef<CustomFile>[] = [
      {
        field: 'index',
        headerName: '',
        width: 50,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (params) => {
          const index = params.api.getRowIndexRelativeToVisibleRows(params.id);
          return index === undefined
            ? 0
            : paginationModel.page * paginationModel.pageSize + index + 1;
        },
        renderCell: (params) => <Box sx={{ pl: 1 }}>{params.value}</Box>,
      },
      {
        field: 'fileName',
        headerName: 'File Name',
        minWidth: 300,
        flex: 1,
        filterable: total > 0,
        sortable: total > 0,
        filterOperators: customOperators,
        disableColumnMenu: true,
        renderCell: (params) => (
          <AutoTruncatingTypographyWithTooltip text={params.value} variant="caption1" />
        ),
      },
    ];

    if (fileView === FileView.PROJECT) {
      baseColumns.push(
        {
          field: 'relativePath',
          headerName: 'Relative Path',
          width: 200,
          filterable: false,
          sortable: total > 0,
          disableColumnMenu: total === 0,
          valueGetter: (params) =>
            params.row.relativePath && params.row.relativePath.length > 0
              ? params.row.relativePath
              : '',
        },
        {
          field: 'createdAt',
          headerName: 'Time of Index',
          width: 180,
          filterable: false,
          sortable: total > 0,
          disableColumnMenu: total === 0,
          valueFormatter: (params) => formatDate(params.value),
        },
        ...(isGraphRagIndex
          ? [
              {
                field: 'graphDocStatus',
                headerName: 'Mapped',
                width: 130,
                filterable: false,
                sortable: false,
                disableColumnMenu: true,
                align: 'center' as const,
                headerAlign: 'center' as const,
                renderCell: ({ row }: { row: CustomFile }) => (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    {renderGraphDocStatusIcon(row.graphDocStatus as GraphDocStatus)}
                  </Box>
                ),
              },
            ]
          : [
              {
                field: 'status',
                headerName: 'Status',
                width: 130,
                filterable: total > 0,
                disableColumnMenu: total === 0,
                filterOperators: customOperators,
                sortable: total > 0,
                valueGetter: (params: GridValueGetterParams<CustomFile>) => params.row.status,
              },
            ]),
        {
          field: 'url',
          headerName: 'URL',
          width: 80,
          sortable: false,
          disableColumnMenu: true,
          filterable: false,
          renderCell: (params) => (
            <StyledLink href={params.row.url || ''} target="_blank">
              <OpenInNew />
            </StyledLink>
          ),
        },
        ...(isGraphRagIndex
          ? [
              {
                field: 'goToGraph',
                headerName: '',
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                width: 140,
                align: 'center' as const,
                headerAlign: 'center' as const,
                renderCell: ({ row }: { row: CustomFile }) => {
                  const hasGraphDocId = Boolean(row.graphDocId);
                  const graphStatus = String(row.graphDocStatus ?? '').toLowerCase();
                  const isCompleted = graphStatus === 'completed';
                  const isDisabled =
                    !hasGraphDocId || !onGoToGraph || (isGraphRagIndex && !isCompleted);

                  return (
                    <Button
                      size="small"
                      variant="outlined"
                      disableElevation
                      disabled={isDisabled}
                      endIcon={<ChevronRight sx={{ fontSize: 18 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onGoToGraph?.(row.graphDocId ?? '');
                      }}
                      sx={{
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        borderRadius: 2.5,
                        '&:hover': {
                          backgroundColor: '#0B0B5B0A',
                        },
                        ...(isCompleted
                          ? {
                              '&:hover': {
                                backgroundColor: '#2E7D321A',
                              },
                            }
                          : {}),

                        '&.Mui-disabled': {
                          borderColor: '#CBD5E1',
                          color: '#94A3B8',
                        },
                      }}
                    >
                      Mapping
                    </Button>
                  );
                },
              },
            ]
          : [])
      );
    } else {
      baseColumns.push(
        {
          field: 'lastModified',
          headerName: 'Modified At',
          width: 180,
          filterable: false,
          sortable: total > 0,
          disableColumnMenu: total === 0,
          valueFormatter: (params) => params.value && formatDate(params.value),
        },
        {
          field: 'createdAt',
          headerName: 'Time of Upload',
          width: 180,
          filterable: false,
          sortable: total > 0,
          disableColumnMenu: total === 0,
          valueFormatter: (params) => formatDate(params.value),
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 130,
          filterable: total > 0,
          filterOperators: customOperators,
          sortable: total > 0,
          disableColumnMenu: total === 0,
          valueGetter: (params) => params.row.status,
        }
      );

      if ([FileView.USER, FileView.FOLDER].includes(fileView)) {
        baseColumns.push({
          field: 'actions',
          headerName: '',
          width: 150,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (params) => (
            <div style={{ display: 'flex', gap: '2px' }}>
              <Tooltip title="Pin">
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFilePin(params.row.id, params.row.pinned);
                  }}
                >
                  {params.row.pinned ? <PushPin /> : <PushPinOutlined />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Rename">
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFileToRename(params.row);
                    setIsRenaming(true);
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFileToDelete(params.row);
                    setIsDeleting(true);
                  }}
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
              {fileView === FileView.USER ? (
                currentFolder?.id !== myFolder.id && (
                  <Tooltip title="Copy to Folder">
                    <IconButton
                      sx={{ padding: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyFileTo(params.row.id, 'folder');
                      }}
                    >
                      <ArrowUpwardOutlined />
                    </IconButton>
                  </Tooltip>
                )
              ) : (
                <Tooltip title="Copy to My Files">
                  <IconButton
                    sx={{ padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyFileTo(params.row.id, 'user');
                    }}
                  >
                    <ArrowDownwardOutlined />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          ),
        });
      }
    }
    return baseColumns;
  }, [
    customOperators,
    fileView,
    handleToggleFilePin,
    paginationModel,
    currentFolder?.id,
    handleCopyFileTo,
    total,
    onGoToGraph,
    isGraphRagIndex,
  ]);

  const handleFilterChange = useCallback(
    (model: GridFilterModel) => {
      setInternalFilterModel(model);
      if (model.items.length > 0) {
        onFilterModelChange?.(model);
      }
    },
    [onFilterModelChange]
  );

  const LoadingOverlay = useCallback(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
          px: 1,
          bgcolor: 'background.paper',
          height: '100%',
        }}
      >
        {[...Array(10)].map((_, index) => (
          <StyledSkeleton
            key={index}
            variant="rectangular"
            width="100%"
            height={45}
            sx={{
              bgcolor: (theme) => theme.palette.grey[100],
              borderRadius: 1,
            }}
          />
        ))}
      </Box>
    ),
    []
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <DataGrid
        rows={files}
        columns={columns}
        rowCount={total}
        loading={isLoading}
        pageSizeOptions={[5, 10, 25, 50]}
        paginationModel={paginationModel}
        paginationMode={'server'}
        filterMode={'server'}
        sortingMode={'server'}
        onPaginationModelChange={onPaginationModelChange}
        onFilterModelChange={handleFilterChange}
        onSortModelChange={onSortModelChange}
        filterModel={
          externalFilterModel?.items && externalFilterModel.items.length > 0
            ? externalFilterModel
            : internalFilterModel
        }
        sortModel={sortModel}
        disableRowSelectionOnClick
        disableColumnSelector
        autoHeight
        filterDebounceMs={300}
        slots={{
          loadingOverlay: LoadingOverlay,
        }}
        sx={{
          height: isLoading ? 635 : 631,
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-overlay': {
            bgcolor: 'background.paper',
          },
        }}
      />

      {fileToRename && (
        <NameDialog
          isOpen={isRenaming}
          setIsOpen={setIsRenaming}
          currentName={fileToRename.fileName.substring(0, fileToRename.fileName.lastIndexOf('.'))}
          onRename={(newFileName) => {
            handleFileRename(
              fileToRename.id,
              newFileName + fileToRename.fileName.substring(fileToRename.fileName.lastIndexOf('.'))
            );
          }}
          type="File"
        />
      )}

      {fileToDelete && (
        <DeleteConfirmationDialog
          isOpen={isDeleting}
          setIsOpen={setIsDeleting}
          onDelete={() => {
            handleFileDelete(fileToDelete.id);
          }}
        />
      )}
    </div>
  );
};

export default FileTable;
